import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilmCard from '@/components/feature/FilmCard';
import { films } from '@/mocks/films';
import { articles } from '@/mocks/articles';
import { searchSuggestions } from '@/mocks/site';

const tabs = [
  { key: 'all', label: '全部' },
  { key: 'film', label: '影视作品' },
  { key: 'article', label: '新闻资讯' },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get('q') ?? '';
  const tab = params.get('tab') ?? 'all';
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
    document.title = keyword
      ? `搜索“${keyword}” - 姐姐的朋友影视内容站`
      : '搜索 - 姐姐的朋友影视内容站';
  }, [keyword]);

  const submit = (value: string, nextTab = tab) => {
    const next = new URLSearchParams();
    if (value.trim()) next.set('q', value.trim());
    if (nextTab !== 'all') next.set('tab', nextTab);
    setParams(next);
  };

  const matchedFilms = useMemo(() => {
    if (!keyword.trim()) return [];
    const k = keyword.trim().toLowerCase();
    return films.filter(
      (f) =>
        f.title.toLowerCase().includes(k) ||
        f.genres.some((g) => g.toLowerCase().includes(k)) ||
        f.region.toLowerCase().includes(k) ||
        f.director.toLowerCase().includes(k) ||
        f.cast.some((c) => c.name.toLowerCase().includes(k)),
    );
  }, [keyword]);

  const matchedArticles = useMemo(() => {
    if (!keyword.trim()) return [];
    const k = keyword.trim().toLowerCase();
    return articles.filter(
      (a) => a.title.toLowerCase().includes(k) || a.summary.toLowerCase().includes(k),
    );
  }, [keyword]);

  const showFilms = tab !== 'article';
  const showArticles = tab !== 'film';
  const totalCount =
    (showFilms ? matchedFilms.length : 0) + (showArticles ? matchedArticles.length : 0);
  const hasQuery = keyword.trim().length > 0;
  const noResult = hasQuery && totalCount === 0;

  const hotFilms = [...films].sort((a, b) => b.heat - a.heat).slice(0, 4);

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="bg-primary-950">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />
          <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50 mt-4 mb-6">
            搜索影视作品与资讯
          </h1>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
            className="flex flex-col sm:flex-row gap-3 max-w-2xl"
          >
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
              <input
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视作品、演员、题材或关键词"
                className="w-full pl-11 pr-4 py-3 rounded-md bg-background-50 border border-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-2-line" />
              搜索
            </button>
          </form>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-xs text-background-200/70">热门搜索</span>
            {searchSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => submit(s)}
                className="px-3 py-1 rounded-full bg-background-50/10 text-background-100 text-xs hover:bg-background-50/20 transition-colors cursor-pointer"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
        {hasQuery ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <p className="text-sm text-foreground-600">
                关键词 <strong className="text-foreground-950">“{keyword}”</strong> · 共找到{' '}
                <strong className="text-accent-600">{totalCount}</strong> 条结果
              </p>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => submit(keyword, t.key)}
                    className={[
                      'px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer',
                      tab === t.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900',
                    ].join(' ')}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {noResult ? (
              <div className="flex flex-col items-center text-center py-12">
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-search-eye-line text-3xl" />
                </span>
                <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-2">
                  未找到相关内容
                </h2>
                <p className="text-sm text-foreground-500 mb-8">
                  换个关键词试试，或看看下面的热门影视推荐
                </p>
                <div className="w-full text-left">
                  <h3 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-3 mb-5">
                    <span className="w-1.5 h-5 rounded-full bg-accent-500" />
                    热门影视推荐
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {hotFilms.map((film) => (
                      <FilmCard key={film.id} film={film} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-12">
                {showFilms && matchedFilms.length > 0 && (
                  <section>
                    <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 flex items-center gap-3 mb-5">
                      <span className="w-1.5 h-5 rounded-full bg-accent-500" />
                      影视作品
                      <span className="text-sm font-normal text-foreground-400">
                        {matchedFilms.length} 条
                      </span>
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                      {matchedFilms.map((film) => (
                        <FilmCard key={film.id} film={film} />
                      ))}
                    </div>
                  </section>
                )}

                {showArticles && matchedArticles.length > 0 && (
                  <section>
                    <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 flex items-center gap-3 mb-5">
                      <span className="w-1.5 h-5 rounded-full bg-accent-500" />
                      新闻资讯
                      <span className="text-sm font-normal text-foreground-400">
                        {matchedArticles.length} 条
                      </span>
                    </h2>
                    <div className="flex flex-col gap-4">
                      {matchedArticles.map((article) => (
                        <Link
                          key={article.id}
                          to={`/article/${article.id}`}
                          className="group flex gap-4 p-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                        >
                          <div className="w-24 h-24 sm:w-32 sm:h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
                            <img
                              src={article.cover}
                              alt={article.title}
                              title={article.title}
                              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                          <div className="flex flex-col justify-center gap-2 min-w-0">
                            <h3 className="font-heading font-semibold text-base text-foreground-950 group-hover:text-primary-700 transition-colors line-clamp-1">
                              {article.title}
                            </h3>
                            <p className="text-sm text-foreground-600 line-clamp-2">{article.summary}</p>
                            <span className="text-xs text-foreground-400 flex items-center gap-1">
                              <i className="ri-time-line" />
                              {article.date}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        ) : (
          <div>
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 flex items-center gap-3 mb-5">
              <span className="w-1.5 h-5 rounded-full bg-accent-500" />
              大家都在看
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {hotFilms.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}